/**
 * /screen-photo-fix — controller. A phone photo of a screen in, a screenshot out.
 *
 * FLOW: photos → each decoded in the worker (HEIC through the shared converter) → the
 * screen is found and graded → the verdict decides what the page may claim:
 *   found / two  — green; the result is made at once (nobody should confirm a frame that
 *                  is already right)
 *   check        — amber «check the frame»; the result is made, the frame is one tap away
 *   none         — amber «not found»; the frame view asks to tap the screen or drag the
 *                  corners, and nothing is made until the person says «straighten»
 *   screenshot   — the file is already digital: nothing to straighten, nothing offered
 *                  for saving (CLAUDE.md «Запускать нечего»)
 * The picture sits first; Save / Copy / Share right under it (or beside it on a wide
 * screen), settings after them. ≥ 1300 px of tool width (the wide workspace on big
 * screens): Frame and Result side by side.
 *
 * WHAT IS BILLED: only a delivered result — saved, copied or shared. Finding, editing,
 * previewing, reading text or a QR never count. The unit is the photo; several shots
 * merged into one picture are one operation (composite key). The same unit delivered
 * again on this page (another format, a copy after a save) is free and is not a second
 * completion.
 */
import { createEngine } from './engine.js';
import { headerSize, jpegTruncated, orientedBitmap } from './header.js';
import { canvasLimits } from '../canvas-limits.js';

const CFG = window.SPF_CFG || {};
const T = (k, r) => {
  let s = CFG.t && CFG.t[k] != null ? String(CFG.t[k]) : k;
  if (r) for (const n in r) s = s.split(':' + n).join(String(r[n]));
  return s;
};
const SLUG = CFG.slug || 'screen-photo-fix';
const $ = (id) => document.getElementById(id);
const RTL = () => (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
const toast = (m, kind) => (window.__toast ? window.__toast(m, kind) : console.log('[toast]', kind, m));
const announce = (m) => { try { window.__announce && window.__announce(m); } catch (_) {} };
const stage = (n, d) => { try { window.__stage && window.__stage(n, d); } catch (_) {} };
const diag = (d) => { try { window.__aeDiagnostic && window.__aeDiagnostic(d); } catch (_) {} };
const feature = (f, d) => { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f }, d || {})); } catch (_) {} };
const complete = (d) => { try { window.__aeToolComplete && window.__aeToolComplete(d); } catch (_) {} };
const runBegin = () => { try { return (window.__runMark && window.__runMark.begin()) || (() => {}); } catch (_) { return () => {}; } };
const ST = window.__toolSettings;
const pref = (k, d) => { try { return ST ? ST.load(SLUG, k, d) : d; } catch (_) { return d; } };
const setPref = (k, v) => { try { ST && ST.save(SLUG, k, v); } catch (_) {} };
const reduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const handleError = (e, label, say) => {
  if (window.__handleError) window.__handleError(e, label, { say });
  else console.error(label, e);
};
const now = () => performance.now();

const MAX_PHOTOS = 8;
const LONG = { photo: null, hd: 1280, fhd: 1920, qhd: 2560, uhd: 3840 };
const S = {
  photos: [], cur: -1, view: 'result',
  merge: { on: false, res: null, keys: null, info: null },
  ratio: pref('ratio', 'auto'), size: LONG[pref('size', 'photo')] !== undefined ? pref('size', 'photo') : 'photo', moire: pref('moire', 'auto'),
  tone: +pref('tone', 100), fmt: pref('fmt', 'png'),
  addMode: false, viaGlare: false, busy: null,
};
let shareOk = false;              // set below, once: can this browser share an image file?
const delivered = new Set();      // units delivered on this page: a second delivery is free and not a new result
let keyN = 0, procGen = 0;

// ── worker ──────────────────────────────────────────────────────────────────
const engine = createEngine(CFG.worker, {
  onReset: () => { for (const p of S.photos) p.inWorker = false; },
  onDied: (e) => diag({ issue: 'worker_died', msg: String((e && e.message) || '').slice(0, 90) }),
});
async function call(cmd, data, opts) {
  try { return await engine.call(cmd, data, opts); }
  catch (e) {
    // the worker lost the photo (it died, or was stopped by Cancel and a later call
    // came): put it back once and ask again
    if (e && e.code === 'PHOTO_GONE' && data && data.key) {
      const p = S.photos.find((x) => x.key === data.key);
      if (p) { p.inWorker = false; await ensureLoaded(p); return engine.call(cmd, data, opts); }
    }
    throw e;
  }
}
async function ensureLoaded(p) {
  if (p.inWorker) return;
  await sendToWorker(p);
}

// ── decoding ────────────────────────────────────────────────────────────────
const HEIC_BRANDS = /^(heic|heix|hevc|hevx|heim|heis|hevm|hevs|mif1|msf1)$/;
async function isHeic(file) {
  if (/^image\/hei[cf]/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '')) return true;
  try { const b = new Uint8Array(await file.slice(0, 12).arrayBuffer()); return String.fromCharCode(b[4], b[5], b[6], b[7]) === 'ftyp' && HEIC_BRANDS.test(String.fromCharCode(b[8], b[9], b[10], b[11])); } catch (_) { return false; }
}
let heicPool = null;
async function heicToBlob(file, maxWidth) {
  if (!heicPool) {
    heicPool = (async () => {
      if (!window.HeicConvert && CFG.lazy && CFG.lazy.heicConvert) await window.__vendorScript(CFG.lazy.heicConvert, { expect: 'HeicConvert', label: 'heic-convert' });
      if (!window.HeicConvert) throw Object.assign(new Error('heic_unavailable'), { code: 'HEIC_FAILED' });
      const pool = window.HeicConvert.create({ workerUrl: CFG.heic.worker, heicToUrl: CFG.heic.heicTo, workers: 1 });
      await pool.ready;
      return pool;
    })();
    heicPool.catch(() => { heicPool = null; });
  }
  const pool = await heicPool;
  // the converter shrinks while it draws: a 48 MP iPhone photo never exists full-size here
  const { blob } = await pool.convert(file, { format: 'image/jpeg', quality: 0.95, keepExif: false, maxWidth: maxWidth || 0 });
  return blob;
}
// The biggest photo the device can hold as pixels: a 50–200 MP phone photo is decoded
// smaller rather than failing the tab. canvasLimits() is the measured canvas ceiling;
// __memBudget the one owner of «how much memory may I assume» (iOS tabs are capped).
function maxPixels() {
  let cap = 24e6;
  try { const L = canvasLimits(); if (L && L.area) cap = Math.min(cap, L.area * 0.9); } catch (_) {}
  const B = window.__memBudget;
  const mem = B ? B.gb() : 4;
  if ((B && B.constrained && B.constrained()) || mem <= 2) cap = Math.min(cap, 8e6); else if (mem <= 4) cap = Math.min(cap, 14e6);
  return cap;
}
// The page's own decoder, for browsers whose workers cannot draw (Safari < 16.4): the
// size is read from the header first, so the bitmap is made small, not shrunk later.
async function decodeOnPage(file, cap) {
  const dims = await headerSize(file);
  let resize = null;
  if (dims && dims.w * dims.h > cap) { const k = Math.sqrt(cap / (dims.w * dims.h)); resize = { resizeWidth: Math.round(dims.w * k), resizeHeight: Math.round(dims.h * k), resizeQuality: 'high' }; }
  let bmp = await orientedBitmap(file, resize);
  if (bmp.width * bmp.height > cap * 1.02) {
    const k = Math.sqrt(cap / (bmp.width * bmp.height));
    const b2 = await createImageBitmap(bmp, { resizeWidth: Math.round(bmp.width * k), resizeHeight: Math.round(bmp.height * k), resizeQuality: 'high' });
    bmp.close(); bmp = b2;
  }
  const c = document.createElement('canvas'); c.width = bmp.width; c.height = bmp.height;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bmp, 0, 0);
  const img = ctx.getImageData(0, 0, c.width, c.height);
  c.width = 0; c.height = 0;
  const srcW = dims ? dims.w : bmp.width, srcH = dims ? dims.h : bmp.height;
  bmp.close();
  return { buf: img.data.buffer, w: img.width, h: img.height, srcW, srcH };
}
async function sendToWorker(p) {
  const src = p.decoded || p.file, cap = maxPixels();
  let r;
  try {
    r = await call('load', { key: p.key, file: src, cap });
  } catch (e) {
    if (!e || (e.code !== 'NO_OFFSCREEN' && e.code !== 'DECODE')) throw e;
    // the worker cannot draw, or its decoder refused: the page tries once itself
    let d;
    try { d = await decodeOnPage(src, cap); } catch (_) { throw Object.assign(new Error('decode'), { code: 'DECODE' }); }
    r = await call('load', { key: p.key, buf: d.buf, w: d.w, h: d.h, srcW: d.srcW, srcH: d.srcH, downscaled: d.w * d.h < d.srcW * d.srcH * 0.98 }, { transfer: [d.buf] });
  }
  p.inWorker = true;
  return r;
}
// focal length in pixels from EXIF (35 mm equivalent): fixes the true shape of a screen
// shot from an angle without guessing the lens. Never blocks the search: only the
// straightening needs it.
async function focal(p) {
  try {
    if (!window.exifr && CFG.lazy && CFG.lazy.exifr) await window.__vendorScript(CFG.lazy.exifr, { expect: 'exifr', label: 'exifr' });
    if (!window.exifr) return null;
    const t = await window.exifr.parse(p.file, { pick: ['FocalLengthIn35mmFormat', 'FocalLengthIn35mmFilm'] });
    const f35 = t && (t.FocalLengthIn35mmFormat || t.FocalLengthIn35mmFilm);
    p.f35 = f35 > 8 && f35 < 400 ? f35 : 0;
    return p.f35 ? p.f35 / 43.27 : null;      // per pixel of the diagonal
  } catch (_) { return null; }
}

// ── photos ──────────────────────────────────────────────────────────────────
function isImage(f) { return f && (/^image\//.test(f.type || '') || /\.(heic|heif|jpe?g|png|webp|gif|avif|bmp)$/i.test(f.name || '')); }
async function addFiles(files, src) {
  const all = Array.from(files || []);
  const list = all.filter(isImage);
  const notes = [];
  if (all.length > list.length) notes.push(T('n_not_image', { n: all.length - list.length }));
  if (!list.length) { if (notes.length) toast(notes.join(' '), 'warning'); return; }
  const replace = !S.addMode && S.photos.length && src !== 'paste';
  const viaGlare = S.viaGlare;
  S.addMode = false; S.viaGlare = false;
  if (replace) clearAll(true);
  $('spfWork').hidden = false;
  $('spfTips').hidden = true;
  const fresh = [];
  let dup = 0;
  let over = 0;
  for (const file of list) {
    const key = (window.__toolLimit && window.__toolLimit.fileKey && window.__toolLimit.fileKey(file)) || ('p' + (++keyN) + '-' + file.size);
    if (S.photos.some((p) => p.key === key)) { dup++; continue; }
    if (S.photos.length >= MAX_PHOTOS) { over++; continue; }
    const p = { key, file, name: file.name || 'photo', src, status: 'loading', verdict: null, quad: null, cands: [], result: null, edits: { n: 0, max: 0 }, tPick: now() };
    S.photos.push(p); fresh.push(p);
  }
  if (over) notes.push(T('n_max', { n: MAX_PHOTOS, skipped: over }));
  if (dup) notes.push(T('n_duplicate', { n: dup }));
  if (notes.length) toast(notes.join(' '), 'info');
  if (!fresh.length) return;
  if (S.cur < 0 || replace || src === 'paste' || src === 'camera') select(S.photos.indexOf(fresh[0]), true);
  renderStrip(); render();
  for (const p of fresh) {
    await openPhoto(p);
    if (cur() === p) render();
    renderStrip();
  }
  syncMerge();
  // shots added from the glare hint are FOR merging: merge them right away
  if (viaGlare && readyForMerge().length >= 2) { S.merge.on = true; $('spfMerge').checked = true; syncMerge(); return processMerged(); }
  const p = cur();
  if (p && p.status === 'ready' && !p.result && wantsResult(p)) processPhoto(p);
}

async function openPhoto(p) {
  const t0 = now();
  try {
    busy(T('b_opening'));
    p.focal = focal(p);
    if (await isHeic(p.file)) {
      p.heic = true;
      const cap = maxPixels();
      p.decoded = await heicToBlob(p.file, Math.floor(Math.sqrt(cap * 0.75)));
    }
    const r = await sendToWorker(p);
    Object.assign(p, { w: r.w, h: r.h, srcW: r.srcW, srcH: r.srcH, downscaled: r.downscaled, display: r.display || null });
    if (r.thumb) p.thumb = r.thumb;
    if (!p.display) p.display = await orientedBitmap(p.decoded || p.file, { resizeWidth: Math.min(2048, r.w), resizeQuality: 'high' }).catch(() => null);
    const mp = +(p.w * p.h / 1e6).toFixed(1);
    stage('photo_loaded', { mp });
    const f = await Promise.race([p.focal, new Promise((res) => setTimeout(() => res(undefined), 30))]);
    feature('photo', { mp, src: p.src, heic: p.heic ? 1 : 0, f35: f === undefined ? -1 : (p.f35 || 0), decode_ms: Math.round(now() - t0), downscaled: p.downscaled ? 1 : 0 });
    busy(T('b_finding'));
    await detectPhoto(p);
    p.status = 'ready';
    if (cur() === p && !S.merge.on) S.view = wantsResult(p) || p.verdict === 'screenshot' ? 'result' : 'frame';
  } catch (e) {
    p.status = 'error';
    let say;
    if (e && e.code === 'DECODE') say = (await jpegTruncated(p.file)) ? T('decode_truncated') : T('decode_failed');
    else if (e && e.code === 'HEIC_FAILED') say = T('heic_failed');
    else if (e && e.code === 'OLD_ENGINE') say = T('err_old_browser');
    else say = T('err_worker');
    p.error = say;
    const ext = ((p.name.match(/\.([a-z0-9]{1,5})$/i) || [])[1] || '').toLowerCase();
    diag({ issue: 'fail', stage: 'load', code: (e && e.code) || 'ERR', ext, mp: p.w ? +(p.w * p.h / 1e6).toFixed(1) : 0 });
    handleError(e, 'spf_load', say);
  } finally { busy(null); }
}

async function detectPhoto(p, point) {
  const t0 = now();
  const r = await call('detect', { key: p.key, point: point || null });
  const quads = (r.quads || []).map((c) => ({ quad: c.quad, virtual: c.virtual, score: c.score, ring: c.ring }));
  if (point) {
    if (!quads.length) return false;
    p.cands = quads; p.quad = cp(quads[0].quad); p.verdict = 'found'; p.tapped = true;
  } else {
    p.verdict = r.verdict; p.ev = r.evidence;
    p.cands = quads;
    p.quad = quads.length ? cp(quads[0].quad) : (r.suggest ? cp(r.suggest) : inset(p));
    p.suggested = !quads.length;
  }
  p.auto = cp(p.quad);
  p.edited = false;
  const top = quads[0];
  feature('detect', { verdict: p.verdict, score: top ? top.score : 0, ms: Math.round(now() - t0), tap: point ? 1 : 0 });
  stage('screen_found', { found: p.verdict, two: quads.length > 1 ? 1 : 0, tap: point ? 1 : 0 });
  return true;
}
const cp = (q) => q.map((v) => ({ x: v.x, y: v.y }));
function inset(p) { const dx = p.w * 0.1, dy = p.h * 0.1; return [{ x: dx, y: dy }, { x: p.w - dx, y: dy }, { x: p.w - dx, y: p.h - dy }, { x: dx, y: p.h - dy }]; }
function whole(p) { return [{ x: 0, y: 0 }, { x: p.w, y: 0 }, { x: p.w, y: p.h }, { x: 0, y: p.h }]; }
const wantsResult = (p) => p.verdict === 'found' || p.verdict === 'two' || p.verdict === 'check' || p.accepted;

function select(i, silent) {
  S.cur = i;
  const p = cur();
  if (!S.merge.on) S.view = p && p.result ? 'result' : (p && !wantsResult(p) ? 'frame' : S.view);
  if (!silent) { renderStrip(); render(); if (p && p.status === 'ready' && !p.result && wantsResult(p) && !S.merge.on) processPhoto(p); }
}
function cur() { return S.photos[S.cur] || null; }

function clearAll(keepWork) {
  engine.cancel();
  for (const p of S.photos) releasePhoto(p);
  S.photos = []; S.cur = -1; dropMerge();
  if (!keepWork) { $('spfWork').hidden = true; $('spfTips').hidden = false; }
  renderStrip();
}
function releasePhoto(p) {
  for (const k of ['display', 'thumb']) { try { p[k] && p[k].close && p[k].close(); } catch (_) {} p[k] = null; }
  releaseResult(p.result); p.result = null; p.share = null;
}
function releaseResult(r) {
  if (!r) return;
  for (const k of ['img', 'before']) { const v = r[k]; if (!v) continue; try { if (v.close) v.close(); else { v.width = 0; v.height = 0; } } catch (_) {} }
}
function dropMerge() { releaseResult(S.merge.res); S.merge.res = null; S.merge.keys = null; S.merge.info = null; S.merge.share = null; }
function removePhoto(i) {
  const p = S.photos[i]; if (!p) return;
  if (S.busy) engine.cancel();
  engine.call('drop', { key: p.key }).catch(() => {});
  releasePhoto(p);
  S.photos.splice(i, 1);
  const wasMerged = S.merge.on;
  dropMerge();
  if (!S.photos.length) { clearAll(); try { const z = $('spfUpload'); z.__uploaderRestore ? z.__uploaderRestore(true) : null; } catch (_) {} render(); return; }
  S.cur = Math.min(S.cur, S.photos.length - 1);
  syncMerge();
  renderStrip(); render();
  // still merged with what is left, or back to this photo's own result: never a dead end
  if (wasMerged && S.merge.on) processMerged();
  else { const c = cur(); if (c && c.status === 'ready' && !c.result && wantsResult(c)) processPhoto(c); }
}

// ── processing ──────────────────────────────────────────────────────────────
let procTimer = 0;
function scheduleProcess() { clearTimeout(procTimer); procTimer = setTimeout(() => { const p = cur(); if (S.merge.on) processMerged(); else if (p) processPhoto(p); }, 300); }
function opts(p) {
  return { quad: p.quad, ratio: S.ratio === 'auto' ? null : +S.ratio, long: LONG[S.size] || null, moire: S.moire, tone: S.tone / 100, maxEdge: 3840 };
}
async function processPhoto(p) {
  if (S.merge.on) return processMerged();
  if (!p || !p.quad || p.status !== 'ready') return;
  const gen = ++procGen;
  const end = runBegin();
  const t0 = now();
  busy(T('b_cleaning'), 0, true);
  try {
    await ensureLoaded(p);
    const fFrac = await p.focal;
    const fPx = fFrac ? fFrac * Math.hypot(p.w, p.h) : null;
    const r = await call('process', Object.assign({ key: p.key, withBefore: true, fPx }, opts(p)), { onProgress: (f) => gen === procGen && busyProgress(f) });
    if (gen !== procGen) { releaseResult(toResult(r)); return; }
    const firstTime = !p.result && !p.everShown;
    releaseResult(p.result); p.share = null;
    p.result = toResult(r);
    p.everShown = true;
    const info = r.info || {};
    const size = info.size || {};
    feature('process', { ms: Math.round(now() - t0), moire_ms: info.moireMs || 0, warp_ms: info.warpMs || 0, tone: (info.tone && info.tone.mode) || 'off', ratio: size.name || (size.ratio ? +size.ratio.toFixed(3) : 0), snapped: size.snapped ? 1 : 0, from_exif: fPx ? 1 : 0, out_w: r.W, out_h: r.H, glare_frac: info.glare ? info.glare.frac : 0, glare_shown: info.glare ? 1 : 0, cached: info.cached || 'none' });
    stage('processed', { ms: Math.round(now() - t0), w: r.W, h: r.H, tone: info.tone && info.tone.mode });
    // a person moving corners stays on the frame: the result is ready on the other tab
    const editing = S.view === 'frame' && p.edited && !firstTime;
    if (!editing) S.view = 'result';
    if (firstTime && cur() === p && !reduced() && mode() === 'tabs' && !editing) lift(p); else render();
    announce(T('ready'));
    prepareShare();
  } catch (e) {
    if (gen !== procGen) return;
    if (e && e.code === 'CANCELLED') { toast(T('cancelled'), 'info'); render(); return; }
    diag({ issue: 'fail', stage: 'process', code: (e && e.code) || 'ERR', mp: p.w ? +(p.w * p.h / 1e6).toFixed(1) : 0 });
    handleError(e, 'spf_process', T('err_process'));
  } finally { end(); if (gen === procGen) busy(null); }
}
function toResult(r) {
  const img = r.bmp || bufCanvas(r.buf, r.W, r.H);
  const before = r.beforeBmp || (r.before ? bufCanvas(r.before, r.W, r.H) : null);
  return { img, before, W: r.W, H: r.H, info: r.info || {} };
}
function bufCanvas(buf, W, H) {
  if (!buf) return null;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(buf), W, H), 0, 0);
  return c;
}
const readyForMerge = () => S.photos.filter((p) => p.status === 'ready' && p.quad && p.verdict !== 'screenshot');
async function processMerged() {
  const ready = readyForMerge();
  if (ready.length < 2) { S.merge.on = false; $('spfMerge').checked = false; syncMerge(); return processPhoto(cur()); }
  const gen = ++procGen;
  const end = runBegin();
  const t0 = now();
  busy(T('b_merging', { n: ready.length }), 0, true);
  try {
    for (const p of ready) await ensureLoaded(p);
    const fFrac = await ready[0].focal;
    const r = await call('fuse', { keys: ready.map((p) => p.key), quads: ready.map((p) => p.quad), fPxs: [fFrac ? fFrac * Math.hypot(ready[0].w, ready[0].h) : null], ratio: S.ratio === 'auto' ? null : +S.ratio, long: LONG[S.size] || null, moire: S.moire, tone: S.tone / 100 }, { onProgress: (f) => gen === procGen && busyProgress(f) });
    if (gen !== procGen) { releaseResult(r.W ? toResult(r) : null); return; }
    const info = r.info || {};
    for (const p of S.photos) p.mismatch = (info.mismatch || []).includes(p.key);
    feature('merge', { n: ready.length, used: (info.used || []).length, ms: Math.round(now() - t0), mismatch: (info.mismatch || []).length });
    if (info.refused) {
      S.merge.on = false; $('spfMerge').checked = false; dropMerge(); syncMerge();
      toast(T('merge_refused'), 'warning');
      renderStrip(); render();
      return processPhoto(cur());
    }
    releaseResult(S.merge.res); S.merge.share = null;
    S.merge.res = toResult(r); S.merge.keys = info.used; S.merge.info = info;
    S.view = 'result'; renderStrip(); render();
    announce(T('merged'));
    prepareShare();
  } catch (e) {
    if (gen !== procGen) return;
    if (e && e.code === 'CANCELLED') { toast(T('cancelled'), 'info'); render(); return; }
    diag({ issue: 'fail', stage: 'merge', code: (e && e.code) || 'ERR' });
    handleError(e, 'spf_fuse', T('err_process'));
  } finally { end(); if (gen === procGen) busy(null); }
}

// ── layout: one picture box per photo, never resized by switching views ─────
const panes = $('spfPanes'), stF = $('spfStageF'), stR = $('spfStageR');
const cvF = $('spfCanvasF'), cvR = $('spfCanvasR'), cvB = $('spfBefore'), overlay = $('spfOverlay'), handles = $('spfHandles');
const PAD = 22;                 // the handles' radius: a corner on the photo's edge stays reachable
let fit = null;                 // frame view: source px → stage px
function mode() { return panes.clientWidth >= 1100 ? 'duo' : 'tabs'; }
function maxH() { return Math.max(240, Math.min(window.innerHeight * 0.7, 720)); }
function sizeStages(p, res) {
  const m = mode();
  panes.dataset.mode = m;
  const wF = stF.parentElement.clientWidth || panes.clientWidth;
  const aPhoto = p && p.w ? Math.min(2.4, Math.max(0.5, p.w / p.h)) : 4 / 3;
  if (m === 'tabs') {
    // the SAME box for both views: switching Frame ↔ Result never moves the page (QA c1)
    const h = Math.round(Math.max(220, Math.min(maxH(), wF / aPhoto)));
    stF.style.height = h + 'px'; stR.style.height = h + 'px';
  } else {
    const aRes = res ? Math.min(2.6, Math.max(0.4, res.W / res.H)) : aPhoto;
    const h = Math.round(Math.max(240, Math.min(maxH(), wF / aPhoto, (stR.parentElement.clientWidth || wF) / aRes)));
    stF.style.height = h + 'px'; stR.style.height = h + 'px';
  }
}
function render() {
  const p = cur();
  renderSide();
  if (!p) { overlay.innerHTML = ''; handles.innerHTML = ''; return; }
  const res = resultNow();
  sizeStages(p, res);
  const m = mode();
  const original = !res && p.verdict === 'screenshot' && !p.accepted && p.status === 'ready';
  const view = m === 'duo' ? 'both' : (S.view === 'result' && (res || original) ? 'result' : 'frame');
  for (const b of document.querySelectorAll('[data-view]')) { const on = b.dataset.view === (view === 'both' ? 'result' : view); b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); }
  $('spfTabs').hidden = m === 'duo';
  $('spfPaneF').hidden = view === 'result';
  $('spfPaneR').hidden = view === 'frame';
  if (view !== 'result') drawFrame(p); else { overlay.innerHTML = ''; handles.innerHTML = ''; }
  if (view !== 'frame') drawResult(res);
  $('spfGlare').hidden = !(res && !S.merge.on && res.info && res.info.glare);
  syncShapeNote(res && res.info && res.info.size, res);
  hint(view);
  $('spfReset').hidden = view === 'result' || !p.auto || !p.edited;
}
function hint(view) {
  const p = cur(), h = $('spfHint');
  let t = '', warn = false;
  if (p && p.status === 'ready' && view !== 'result') {
    if (p.verdict === 'none' && !p.accepted) { t = T('h_frame_none'); warn = true; }
    else if (p.cands.length > 1) t = T('h_frame_two');
    else t = T('h_frame');
  } else if (view === 'result' && resultNow()) t = T('h_result');
  h.textContent = t; h.classList.toggle('is-warn', warn); h.hidden = !t;
}

function drawFrame(p) {
  overlay.innerHTML = ''; handles.innerHTML = '';
  const empty = $('spfEmpty');
  empty.hidden = !(p && p.status === 'error');
  if (p && p.status === 'error') empty.textContent = p.error || T('decode_failed');
  if (!p || !p.display) { cvF.width = 1; cvF.height = 1; cvF.style.width = '0'; return; }
  const W = stF.clientWidth, H = stF.clientHeight;
  const s = Math.min((W - 2 * PAD) / p.w, (H - 2 * PAD) / p.h);
  const cw = Math.max(1, Math.round(p.w * s)), ch = Math.max(1, Math.round(p.h * s));
  const ox = Math.round((W - cw) / 2), oy = Math.round((H - ch) / 2);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const bw = Math.min(p.display.width, Math.round(cw * dpr)), bh = Math.round(bw * ch / cw);
  if (cvF.width !== bw || cvF.height !== bh) { cvF.width = bw; cvF.height = bh; }
  Object.assign(cvF.style, { width: cw + 'px', height: ch + 'px', left: ox + 'px', top: oy + 'px' });
  const ctx = cvF.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(p.display, 0, 0, bw, bh);
  fit = { s, ox, oy, w: cw, h: ch };
  overlay.setAttribute('viewBox', `0 0 ${W} ${H}`);
  Object.assign(overlay.style, { left: '0', top: '0', width: W + 'px', height: H + 'px' });
  if (!p.quad || p.status !== 'ready') return;
  p.cands.slice(1).forEach((c, k) => {
    const alt = svg('polygon', { class: 'spf-alt', points: pts(c.quad) });
    alt.addEventListener('click', (e) => { e.stopPropagation(); swapCandidate(k + 1); });
    overlay.appendChild(alt);
  });
  const weak = p.verdict === 'none' && !p.accepted && !p.edited;
  overlay.appendChild(svg('polygon', { class: 'spf-edge-shade', points: pts(p.quad) }));
  overlay.appendChild(svg('polygon', { class: 'spf-edge' + (weak ? ' is-weak' : ''), points: pts(p.quad) }));
  const names = ['corner_tl', 'corner_tr', 'corner_br', 'corner_bl'];
  p.quad.forEach((q, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'spf-handle'; b.dataset.i = i;
    b.setAttribute('aria-label', T(names[i]));
    b.style.left = (fit.ox + q.x * fit.s) + 'px'; b.style.top = (fit.oy + q.y * fit.s) + 'px';
    bindHandle(b, i);
    handles.appendChild(b);
  });
}
function pts(q) { return q.map((v) => `${(fit.ox + v.x * fit.s).toFixed(1)},${(fit.oy + v.y * fit.s).toFixed(1)}`).join(' '); }
function svg(tag, attrs) { const el = document.createElementNS('http://www.w3.org/2000/svg', tag); for (const k in attrs) el.setAttribute(k, attrs[k]); return el; }

function swapCandidate(k) {
  const p = cur(); if (!p || !p.cands[k]) return;
  const c = p.cands.splice(k, 1)[0]; p.cands.unshift(c);
  p.quad = cp(c.quad); p.auto = cp(p.quad); p.edited = false;
  feature('frame_edit', { kind: 'swap', n: ++p.edits.n, max_shift_pct: 0 });
  releaseResult(p.result); p.result = null;
  render(); scheduleProcess();
}

// ── corner editing ──────────────────────────────────────────────────────────
let drag = null;
function bindHandle(b, i) {
  b.addEventListener('pointerdown', (e) => {
    e.preventDefault(); e.stopPropagation();
    try { b.setPointerCapture(e.pointerId); } catch (_) {}
    b.classList.add('is-active');
    drag = { i, id: e.pointerId, moved: false, from: cp(cur().quad) };
    loupe(i);
  });
  b.addEventListener('pointermove', (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    const p = cur(), r = stF.getBoundingClientRect();
    const x = (e.clientX - r.left - fit.ox) / fit.s, y = (e.clientY - r.top - fit.oy) / fit.s;
    p.quad[i] = { x: Math.max(0, Math.min(p.w, x)), y: Math.max(0, Math.min(p.h, y)) };
    drag.moved = true;
    moveHandle(i); loupe(i);
  });
  const end = (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    b.classList.remove('is-active');
    $('spfLoupe').hidden = true;
    const d = drag; drag = null;
    if (d.moved) edited('drag', d.from, i);
  };
  b.addEventListener('pointerup', end); b.addEventListener('pointercancel', end);
  b.addEventListener('keydown', (e) => {
    const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (!d) return;
    e.preventDefault();
    const p = cur(), from = cp(p.quad), k = (e.shiftKey ? 10 : 1) / fit.s;
    p.quad[i] = { x: Math.max(0, Math.min(p.w, p.quad[i].x + d[0] * k)), y: Math.max(0, Math.min(p.h, p.quad[i].y + d[1] * k)) };
    moveHandle(i); loupe(i);
    edited('key', from, i);
  });
  b.addEventListener('blur', () => { $('spfLoupe').hidden = true; });
}
function edited(kind, from, i) {
  const p = cur();
  p.edited = true; p.accepted = true;
  const shift = Math.hypot(p.quad[i].x - from[i].x, p.quad[i].y - from[i].y) / Math.max(p.w, p.h) * 100;
  p.edits.n++; p.edits.max = Math.max(p.edits.max, shift);
  feature('frame_edit', { kind, n: p.edits.n, max_shift_pct: +p.edits.max.toFixed(1) });
  releaseResult(p.result); p.result = null; p.share = null;
  $('spfReset').hidden = false;
  renderSide(); hint('frame');
  scheduleProcess();
}
function moveHandle(i) {
  const p = cur(), b = handles.children[i];
  if (b) { b.style.left = (fit.ox + p.quad[i].x * fit.s) + 'px'; b.style.top = (fit.oy + p.quad[i].y * fit.s) + 'px'; }
  for (const poly of overlay.querySelectorAll('.spf-edge, .spf-edge-shade')) poly.setAttribute('points', pts(p.quad));
}
// The loupe: the photo around the dragged corner at FULL resolution (from the worker),
// with the crosshair and the frame's own lines, on the far side of the stage from the
// finger. Drawn at once from the display copy, then sharpened when the patch arrives.
let loupeSeq = 0;
function loupe(i) {
  const p = cur(), L = $('spfLoupe'); if (!p || !p.display) return;
  L.hidden = false;
  const q = p.quad[i], R = Math.max(16, Math.round(Math.min(p.w, p.h) * 0.025));
  const left = (fit.ox + q.x * fit.s) < stF.clientWidth / 2;
  L.style.left = left ? 'auto' : '10px'; L.style.right = left ? '10px' : 'auto';
  const ctx = L.getContext('2d'), n = L.width;
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, n, n);
  const k = p.display.width / p.w;
  ctx.drawImage(p.display, (q.x - R) * k, (q.y - R) * k, 2 * R * k, 2 * R * k, 0, 0, n, n);
  loupeLines(ctx, p, q, R, n);
  const my = ++loupeSeq;
  if (!S.busy) call('patch', { key: p.key, x: q.x, y: q.y, r: R }).then((r) => {
    if (my !== loupeSeq || L.hidden) return;
    const img = new ImageData(new Uint8ClampedArray(r.buf), r.n, r.n);
    const t = document.createElement('canvas'); t.width = r.n; t.height = r.n; t.getContext('2d').putImageData(img, 0, 0);
    ctx.drawImage(t, 0, 0, n, n);
    loupeLines(ctx, p, q, R, n);
  }).catch(() => {});
}
function loupeLines(ctx, p, q, R, n) {
  const k = n / (2 * R), X = (x) => (x - (q.x - R)) * k, Y = (y) => (y - (q.y - R)) * k;
  ctx.save();
  ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(0,0,0,.6)';
  const line = () => { ctx.beginPath(); p.quad.forEach((v, j) => (j ? ctx.lineTo(X(v.x), Y(v.y)) : ctx.moveTo(X(v.x), Y(v.y)))); ctx.closePath(); ctx.stroke(); };
  line();
  ctx.lineWidth = 1; ctx.strokeStyle = getComputedStyle(stF).getPropertyValue('--spf-line').trim() || '#7cf3ff';
  line();
  // the crosshair at the corner itself
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(n / 2 - 12, n / 2 + 0.5); ctx.lineTo(n / 2 + 12, n / 2 + 0.5); ctx.moveTo(n / 2 + 0.5, n / 2 - 12); ctx.lineTo(n / 2 + 0.5, n / 2 + 12); ctx.stroke();
  ctx.restore();
}
stF.addEventListener('click', (e) => {
  const p = cur();
  if (!p || p.status !== 'ready' || !fit || S.busy || e.target.closest('.spf-handle,.spf-alt')) return;
  const r = stF.getBoundingClientRect();
  const x = (e.clientX - r.left - fit.ox) / fit.s, y = (e.clientY - r.top - fit.oy) / fit.s;
  if (x < 0 || y < 0 || x > p.w || y > p.h) return;
  if ((p.verdict === 'found' || p.verdict === 'two' || p.accepted) && inQuad(p.quad, { x, y })) return;     // a tap inside the frame is not a search
  busy(T('b_finding'));
  detectPhoto(p, { x, y }).then((ok) => {
    busy(null);
    if (!ok) { toast(T('pick_screen'), 'info'); return; }
    p.accepted = true;
    feature('frame_edit', { kind: 'tap', n: ++p.edits.n, max_shift_pct: 0 });
    releaseResult(p.result); p.result = null;
    render(); scheduleProcess();
  }).catch((err) => { busy(null); handleError(err, 'spf_tap', T('err_worker')); });
});
function inQuad(q, pt) {
  let s = 0;
  for (let i = 0; i < 4; i++) { const a = q[i], b = q[(i + 1) % 4]; const z = (b.x - a.x) * (pt.y - a.y) - (b.y - a.y) * (pt.x - a.x); const sg = z > 0 ? 1 : -1; if (!s) s = sg; else if (sg !== s) return false; }
  return true;
}
$('spfReset').addEventListener('click', () => {
  const p = cur(); if (!p || !p.auto) return;
  p.quad = cp(p.auto); p.edited = false;
  feature('frame_edit', { kind: 'reset', n: ++p.edits.n, max_shift_pct: 0 });
  releaseResult(p.result); p.result = null;
  render(); scheduleProcess();
});

// The one orchestrated moment: the found outline lifts out of the photo and becomes the
// straight screenshot (320 ms; never with reduced motion).
function lift(p) {
  const from = p.quad.map((q) => ({ x: fit.ox + q.x * fit.s, y: fit.oy + q.y * fit.s }));
  render();
  const r = cvR.getBoundingClientRect(), box = stR.getBoundingClientRect();
  const to = [{ x: r.left - box.left, y: r.top - box.top }, { x: r.right - box.left, y: r.top - box.top }, { x: r.right - box.left, y: r.bottom - box.top }, { x: r.left - box.left, y: r.bottom - box.top }];
  const ghost = svg('svg', { class: 'spf-overlay spf-ghost', style: `left:0;top:0;width:${box.width}px;height:${box.height}px`, viewBox: `0 0 ${box.width} ${box.height}` });
  const poly = svg('polygon', { class: 'spf-edge', points: '' }); ghost.appendChild(poly);
  stR.appendChild(ghost);
  cvR.style.opacity = '0';
  const t0 = now();
  const step = (t) => {
    const k = Math.min(1, (t - t0) / 320), e = 1 - Math.pow(1 - k, 3);
    poly.setAttribute('points', from.map((f, i) => `${f.x + (to[i].x - f.x) * e},${f.y + (to[i].y - f.y) * e}`).join(' '));
    cvR.style.opacity = String(Math.max(0, (k - 0.35) / 0.65));
    if (k < 1) requestAnimationFrame(step); else { ghost.remove(); cvR.style.opacity = ''; }
  };
  requestAnimationFrame(step);
}

// ── result view + compare ───────────────────────────────────────────────────
let cutPos = 50, resFit = null;
function drawResult(res) {
  const p = cur();
  const showOriginal = !res && p && p.verdict === 'screenshot' && !p.accepted && p.display;
  if (!res && !showOriginal) { cvR.style.width = '0'; cvB.hidden = true; $('spfCut').hidden = true; return; }
  const W = stR.clientWidth, H = stR.clientHeight;
  const iw = res ? res.W : p.w, ih = res ? res.H : p.h;
  const s = Math.min(W / iw, H / ih);
  const cw = Math.max(1, Math.round(iw * s)), ch = Math.max(1, Math.round(ih * s));
  const ox = Math.round((W - cw) / 2), oy = Math.round((H - ch) / 2);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  resFit = { ox, oy, w: cw, h: ch };
  const paint = (c, from) => {
    const bw = Math.min(from.width, Math.round(cw * dpr)), bh = Math.max(1, Math.round(bw * ch / cw));
    if (c.width !== bw || c.height !== bh) { c.width = bw; c.height = bh; }
    Object.assign(c.style, { width: cw + 'px', height: ch + 'px', left: ox + 'px', top: oy + 'px' });
    const ctx = c.getContext('2d'); ctx.imageSmoothingQuality = 'high'; ctx.drawImage(from, 0, 0, bw, bh);
  };
  paint(cvR, res ? res.img : p.display);
  const compare = !!(res && res.before) && mode() === 'tabs';
  cvB.hidden = !compare; $('spfCut').hidden = !compare;
  if (compare) { paint(cvB, res.before); setCut(cutPos); }
}
function setCut(v) {
  cutPos = Math.max(0, Math.min(100, v));
  if (!resFit) return;
  // one integer pixel for the clip AND the line: two roundings drew two lines in WebKit
  const px = Math.round(resFit.w * cutPos / 100);
  const rtl = RTL();
  cvB.style.clipPath = rtl ? `inset(0 0 0 ${px}px)` : `inset(0 ${resFit.w - px}px 0 0)`;
  const x = resFit.ox + px;
  $('spfCut').style.left = x + 'px';
  $('spfCut').style.top = resFit.oy + 'px'; $('spfCut').style.height = resFit.h + 'px';
  $('spfCut').setAttribute('aria-valuenow', String(Math.round(cutPos)));
  $('spfCut').setAttribute('aria-valuetext', rtl ? T('compare_value_rtl', { n: Math.round(cutPos) }) : T('compare_value', { n: Math.round(cutPos) }));
}
(function bindCut() {
  const cut = $('spfCut');
  let on = false;
  const at = (e) => { const r = cvR.getBoundingClientRect(); setCut((e.clientX - r.left) / r.width * 100); };
  cut.addEventListener('pointerdown', (e) => { on = true; try { cut.setPointerCapture(e.pointerId); } catch (_) {} at(e); e.preventDefault(); });
  cut.addEventListener('pointermove', (e) => { if (on) at(e); });
  cut.addEventListener('pointerup', () => { if (on) feature('compare'); on = false; });
  cut.addEventListener('pointercancel', () => { on = false; });
  cut.addEventListener('keydown', (e) => {
    const r = RTL() ? -1 : 1;
    const d = { ArrowLeft: -5 * r, ArrowRight: 5 * r, Home: -100, End: 100 }[e.key];
    if (d == null) return; e.preventDefault(); setCut(cutPos + d);
  });
})();

// ── the side: verdict → delivery → extras → settings ────────────────────────
function resultNow() { return S.merge.on ? S.merge.res : (cur() && cur().result); }
function verdictOf() {
  const p = cur();
  if (!p) return null;
  if (p.status === 'loading') return { tone: 'info', text: T('v_loading'), spin: true };
  if (p.status === 'error') return { tone: 'bad', text: p.error || T('decode_failed'), acts: [['act_pick_other', pickOther], ['act_remove', () => removePhoto(S.cur)]] };
  if (S.merge.on && S.merge.res) {
    const n = (S.merge.keys || []).length, mm = S.photos.filter((x) => x.mismatch).map((x) => S.photos.indexOf(x) + 1);
    return { tone: mm.length ? 'warn' : 'ok', text: T('v_merged', { n }), sub: mm.length ? T('v_merge_mismatch', { list: mm.join(', ') }) : '' };
  }
  const notes = [];
  if (p.w && Math.max(p.w, p.h) < 900) notes.push(T('n_tiny', { w: p.w, h: p.h }));
  if (p.downscaled) notes.push(T('n_downscaled', { from: +(p.srcW * p.srcH / 1e6).toFixed(0), to: +(p.w * p.h / 1e6).toFixed(0) }));
  const sub = notes.join(' ');
  if (p.verdict === 'screenshot' && !p.accepted) return { tone: 'info', text: T('v_screenshot'), sub: T('v_screenshot_sub'), acts: [['act_process_anyway', () => accept(p, whole(p))]] };
  if (p.verdict === 'none' && !p.accepted) return { tone: 'warn', text: T('v_none'), sub: [T('v_none_sub'), sub].filter(Boolean).join(' '), acts: [['act_straighten', () => accept(p)], ['act_whole', () => accept(p, whole(p))]] };
  if (p.verdict === 'check' && !p.accepted) return { tone: 'warn', text: T('v_check'), sub, acts: [['act_check_frame', showFrame]] };
  if (p.cands.length > 1 && !p.accepted) return { tone: 'ok', text: T('v_two'), sub, acts: [['act_show_frame', showFrame]] };
  // the tabs under the picture already lead to the frame: no second button for it here
  return { tone: 'ok', text: p.accepted ? T('v_yours') : T('v_found'), sub: sub || (p.accepted ? '' : T('v_found_sub')), acts: [] };
}
function accept(p, q) {
  if (q) { p.quad = q; p.auto = p.auto || cp(q); }
  p.accepted = true;
  feature('frame_edit', { kind: q ? 'whole' : 'confirm', n: ++p.edits.n, max_shift_pct: 0 });
  releaseResult(p.result); p.result = null;
  processPhoto(p);
}
function showFrame() { S.view = 'frame'; render(); try { $('spfStageF').focus({ preventScroll: true }); } catch (_) {} }
function pickOther() { const i = fileInput(); if (i) { S.addMode = false; i.click(); } }

function renderSide() {
  const v = verdictOf(), box = $('spfVerdict');
  box.hidden = !v;
  if (v) {
    box.dataset.tone = v.tone;
    box.classList.toggle('is-busy', !!v.spin);
    $('spfVerdictText').textContent = v.text;
    const sub = $('spfVerdictSub'); sub.textContent = v.sub || ''; sub.hidden = !v.sub;
    const acts = $('spfVerdictActs'); acts.innerHTML = '';
    for (const [k, fn] of v.acts || []) {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn ghost sm'; b.textContent = T(k);
      b.addEventListener('click', fn); acts.appendChild(b);
    }
    acts.hidden = !(v.acts && v.acts.length);
  }
  const p = cur();
  const res = resultNow();
  // nothing to run → the whole offer goes, the verdict stands in its place
  const offer = !!p && p.status === 'ready' && !(p.verdict === 'screenshot' && !p.accepted) && !(p.verdict === 'none' && !p.accepted && !res);
  $('spfDeliver').hidden = !offer;
  for (const id of ['spfSave', 'spfCopy', 'spfShare']) $(id).disabled = !res || !!S.busy;
  $('spfShare').hidden = !shareOk;
  const textable = !!res || (p && p.verdict === 'screenshot' && p.display);
  for (const id of ['spfText', 'spfQr']) $(id).disabled = !textable || !!S.busy;
  $('spfExtras').hidden = !p || p.status !== 'ready';
  $('spfSettings').hidden = !offer;
  $('spfSaveLabel').textContent = T('save_fmt', { fmt: S.fmt.toUpperCase() });
}
function syncShapeNote(size, res) {
  const n = $('spfShapeNote');
  if (!size || !res) { n.textContent = ''; return; }
  const r = size.ratio >= 1 ? size.ratio : 1 / size.ratio;
  const label = size.name || (Math.round(r * 100) / 100 + ':1');
  // isolated: in a right-to-left page «2856×1607» would otherwise read 1607×2856
  n.textContent = (S.ratio === 'auto' ? T('shape_measured', { ratio: '⁦' + label + '⁩' }) + ' ' : '') + `⁦${res.W}×${res.H}⁩`;
}
function renderStrip() {
  const el = $('spfStrip'), list = $('spfThumbs');
  el.hidden = !S.photos.length;
  list.innerHTML = '';
  S.photos.forEach((p, i) => {
    const li = document.createElement('li');
    li.className = 'spf-thumb' + (i === S.cur ? ' is-on' : '') + (p.mismatch && S.merge.on ? ' is-off' : '');
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'spf-thumb-pick';
    b.setAttribute('aria-label', T('photo_n', { n: i + 1 }) + (p.status === 'error' ? ' — ' + T('st_error') : ''));
    if (i === S.cur) b.setAttribute('aria-current', 'true');
    if (p.thumb) {
      const c = document.createElement('canvas'); c.width = p.thumb.width; c.height = p.thumb.height;
      c.getContext('2d').drawImage(p.thumb, 0, 0); b.appendChild(c);
    }
    const st = document.createElement('span'); st.className = 'spf-st spf-st-' + p.status; st.setAttribute('aria-hidden', 'true');
    st.textContent = p.status === 'loading' ? '…' : p.status === 'error' ? '!' : (p.mismatch && S.merge.on ? '≠' : (p.verdict === 'none' && !p.accepted ? '?' : '✓'));
    b.appendChild(st);
    b.addEventListener('click', () => select(i));
    const x = document.createElement('button');
    x.type = 'button'; x.className = 'spf-thumb-x'; x.textContent = '×';
    x.setAttribute('aria-label', T('remove_photo_n', { n: i + 1 }));
    x.addEventListener('click', () => removePhoto(i));
    li.appendChild(b); li.appendChild(x);
    list.appendChild(li);
  });
}
function syncMerge() {
  const n = readyForMerge().length;
  $('spfMergeRow').hidden = n < 2;
  if (n < 2 && S.merge.on) { S.merge.on = false; $('spfMerge').checked = false; dropMerge(); }
}

// ── busy: what is happening, how far, and a way out ─────────────────────────
function busy(text, frac, cancellable) {
  S.busy = text ? { text, cancellable: !!cancellable } : null;
  const b = $('spfBusy'); b.hidden = !text;
  if (text) {
    $('spfBusyText').textContent = text;
    $('spfCancel').hidden = !cancellable;
    busyProgress(frac);
  }
  renderSide();
}
function busyProgress(f) {
  const bar = $('spfProg');
  if (f == null) { bar.parentElement.classList.add('is-indet'); return; }
  bar.parentElement.classList.remove('is-indet');
  bar.style.width = Math.round(Math.max(0.03, Math.min(1, f)) * 100) + '%';
}
$('spfCancel').addEventListener('click', () => { feature('cancel', { what: S.busy && S.busy.text ? 'run' : '' }); procGen++; engine.cancel(); busy(null); toast(T('cancelled'), 'info'); render(); });

// ── settings ────────────────────────────────────────────────────────────────
function bindChips(rootId, attr, key, after) {
  const root = $(rootId);
  const set = (v) => {
    for (const b of root.querySelectorAll(`[data-${attr}]`)) {
      const on = b.dataset[attr] === String(v);
      b.classList.toggle('on', on);
      b.setAttribute(b.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-pressed', on ? 'true' : 'false');
    }
  };
  set(S[key]);
  root.addEventListener('click', (e) => {
    const b = e.target.closest(`[data-${attr}]`); if (!b || b.hidden) return;
    if (S[key] === b.dataset[attr]) return;
    S[key] = b.dataset[attr]; set(S[key]); setPref(key, S[key]);
    feature('setting', { k: key, v: S[key] });
    if (after) after();
  });
  return set;
}
// a setting changes the result, not the photos: the worker's caches make this cheap
const reprocess = () => { for (const p of S.photos) { releaseResult(p.result); p.result = null; p.share = null; } dropMerge(); renderSide(); scheduleProcess(); };
bindChips('spfShape', 'ratio', 'ratio', reprocess);
bindChips('spfMoire', 'moire', 'moire', reprocess);
const setFmt = bindChips('spfFormat', 'fmt', 'fmt', () => { renderSide(); });
$('spfSize').value = S.size;
$('spfSize').addEventListener('change', () => { S.size = $('spfSize').value; setPref('size', S.size); feature('setting', { k: 'size', v: S.size }); reprocess(); });
$('spfTone').value = S.tone; $('spfToneOut').textContent = S.tone + '%';
$('spfTone').addEventListener('input', () => { $('spfToneOut').textContent = $('spfTone').value + '%'; });
$('spfTone').addEventListener('change', () => { S.tone = +$('spfTone').value; setPref('tone', S.tone); feature('setting', { k: 'tone', v: S.tone }); reprocess(); });
$('spfMerge').addEventListener('change', () => {
  S.merge.on = $('spfMerge').checked; dropMerge();
  feature('merge_toggle', { on: S.merge.on ? 1 : 0 });
  if (S.merge.on) processMerged(); else { for (const p of S.photos) p.mismatch = false; renderStrip(); const p = cur(); if (p && p.result) render(); else processPhoto(p); }
});
for (const b of document.querySelectorAll('[data-view]')) b.addEventListener('click', () => {
  S.view = b.dataset.view;
  const p = cur();
  if (S.view === 'result' && p && !resultNow() && wantsResult(p)) processPhoto(p);
  render();
});
const fileInput = () => $('spfUpload') && $('spfUpload').querySelector('input[type=file]');
$('spfMore').addEventListener('click', () => { const i = fileInput(); if (!i) return; S.addMode = true; i.click(); });
$('spfGlareAdd').addEventListener('click', () => { feature('glare_add'); const i = fileInput(); if (!i) return; S.addMode = true; S.viaGlare = true; i.click(); });
let rz = 0;
window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(render, 120); });
window.addEventListener('wide-workspace:change', () => { clearTimeout(rz); rz = setTimeout(render, 60); });

// WebP where the browser can write it (Safari's canvas answers PNG for every type)
let webpOk = true;
try { const c = document.createElement('canvas'); c.width = 2; c.height = 2; webpOk = c.toDataURL('image/webp').indexOf('data:image/webp') === 0; } catch (_) { webpOk = false; }
if (!webpOk) { const w = document.querySelector('[data-fmt="webp"]'); if (w) w.hidden = true; if (S.fmt === 'webp') { S.fmt = 'png'; setFmt('png'); } }

// ── delivery (the only thing that is billed) ────────────────────────────────
const MIME = { png: 'image/png', jpg: 'image/jpeg', webp: 'image/webp' };
const EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };
function unit() {
  if (S.merge.on && S.merge.res) { const k = 'set:' + S.merge.keys.slice().sort().join(','); return { id: k, keys: [k] }; }
  const p = cur(); return p ? { id: p.key, keys: [p.key] } : null;
}
function outName(ext) {
  const p = cur();
  const base = (p && p.name ? p.name.replace(/\.[^.]+$/, '') : 'screen') + '-screenshot';
  const clean = window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(base) : base.replace(/[\\/:*?"<>|]+/g, '_');
  return clean + '.' + ext;
}
function canvasOf(res) {
  if (res.img instanceof HTMLCanvasElement) return { c: res.img, own: false };
  const c = document.createElement('canvas'); c.width = res.W; c.height = res.H;
  c.getContext('2d').drawImage(res.img, 0, 0);
  return { c, own: true };
}
async function blobOf(res, fmt) {
  const { c, own } = canvasOf(res);
  try { return await window.__canvasBlob(c, MIME[fmt], fmt === 'png' ? undefined : 0.93); }
  finally { if (own) { c.width = 0; c.height = 0; } }
}
// One delivery: the cap only for a unit not yet delivered on this page; the completion
// only for the first delivery of a unit (QA m11: re-saves and copies each counted).
function startDelivery() {
  const u = unit(); if (!u) return null;
  if (delivered.has(u.id)) return { u, again: true, commit() {}, abort() {} };
  const TL = window.__toolLimit;
  const run = TL ? TL.begin(SLUG, u.keys) : { commit() {}, abort() {} };
  if (!run) return null;
  return { u, again: false, commit() { run.commit(); }, abort() { run.abort(); } };
}
function delivered_(d, via, fmt) {
  const p = cur(), res = resultNow();
  feature('deliver', { via, fmt, unit_new: d.again ? 0 : 1, auto_frame: p && !p.edited && !p.tapped ? 1 : 0, edits: p ? p.edits.n : 0, verdict: S.merge.on ? 'merged' : (p && p.verdict) || '', ms_since_pick: p ? Math.round(now() - p.tPick) : 0 });
  if (d.again) return;
  delivered.add(d.u.id);
  complete({ via, fmt, w: res.W, h: res.H, merged: S.merge.on ? S.merge.keys.length : 0, moire: S.moire, tone: S.tone, edited: p && p.edited ? 1 : 0, shape: S.ratio, verdict: S.merge.on ? 'merged' : (p && p.verdict) || '' });
}
$('spfSave').addEventListener('click', async () => {
  const res = resultNow(); if (!res) return;
  const d = startDelivery(); if (!d) return;
  try {
    const blob = await blobOf(res, S.fmt);
    // the file is named by what the browser actually wrote, not by what was asked
    const ext = EXT[blob.type] || S.fmt;
    const ok = await window.__downloadFile(blob, outName(ext));
    if (ok === false) { d.abort(); return; }
    d.commit(); delivered_(d, 'save', ext);
  } catch (e) { d.abort(); handleError(e, 'spf_save', T('err_process')); }
});
$('spfCopy').addEventListener('click', async () => {
  const res = resultNow(); if (!res) return;
  const d = startDelivery(); if (!d) return;
  try {
    // the promise goes straight into ClipboardItem: Safari needs the write inside the gesture
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blobOf(res, 'png') })]);
    d.commit(); delivered_(d, 'copy', 'png'); toast(T('copied'), 'success');
  } catch (e) { d.abort(); toast(T('copy_failed'), 'warning'); diag({ issue: 'copy_failed', msg: String((e && e.name) || e).slice(0, 60) }); }
});
// Share: iOS lets navigator.share run only within the tap; an encode awaited before it
// spends the tap and the share fails silently (QA m3). The PNG is made as soon as the
// result is ready, so the tap shares at once; a tap that comes earlier says so.
try { shareOk = !!(navigator.canShare && navigator.share && navigator.canShare({ files: [new File([new Uint8Array(8)], 'x.png', { type: 'image/png' })] })); } catch (_) { shareOk = false; }
function holder() { return S.merge.on ? S.merge : cur(); }
function prepareShare() {
  if (!shareOk) return;
  const h = holder(), res = resultNow();
  if (!h || !res || h.share) return;
  const job = h.share = { file: null, res };
  const run = () => blobOf(res, 'png').then((b) => { if (h.share === job) job.file = new File([b], outName('png'), { type: 'image/png' }); }).catch(() => { if (h.share === job) h.share = null; });
  if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 1200 }); else setTimeout(run, 200);
}
$('spfShare').addEventListener('click', () => {
  const res = resultNow(); if (!res) return;
  const h = holder();
  const file = h && h.share && h.share.res === res ? h.share.file : null;
  if (!file) { prepareShare(); toast(T('share_preparing'), 'info'); return; }
  const d = startDelivery(); if (!d) return;
  navigator.share({ files: [file], title: T('share_title') }).then(() => { d.commit(); delivered_(d, 'share', 'png'); }).catch((e) => {
    d.abort();
    if (e && e.name === 'AbortError') return;
    diag({ issue: 'share_failed', msg: String((e && e.name) || e).slice(0, 60) });
    toast(T('share_failed'), 'warning');
  });
});

// ── text and QR from the result (never billed: the person reads their own photo) ──
let ocrMod = null;
async function sourceCanvas() {
  const res = resultNow();
  if (res) return canvasOf(res);
  const p = cur();
  if (!p) return null;
  // a screenshot left as it is: the original, at its own size
  const bmp = await orientedBitmap(p.decoded || p.file);
  const c = document.createElement('canvas'); c.width = bmp.width; c.height = bmp.height; c.getContext('2d').drawImage(bmp, 0, 0); bmp.close();
  return { c, own: true };
}
$('spfText').addEventListener('click', async () => {
  try {
    if (!ocrMod) ocrMod = await import('./ocr.js');
    await ocrMod.readText({ CFG, T, toast, feature, diag, handleError, busy, busyProgress, runBegin, source: sourceCanvas });
  } catch (e) { handleError(e, 'spf_ocr', T('ocr_failed')); busy(null); }
});
$('spfQr').addEventListener('click', async () => {
  try {
    if (!ocrMod) ocrMod = await import('./ocr.js');
    await ocrMod.readQr({ CFG, T, toast, feature, diag, source: sourceCanvas });
  } catch (e) { toast(T('qr_none'), 'info'); diag({ issue: 'qr_failed', msg: String((e && e.message) || e).slice(0, 60) }); }
});

// ── wiring the shared uploader ──────────────────────────────────────────────
(function bindUploader() {
  const el = $('spfUpload'); if (!el) return;
  const handler = (files, src) => { stage('upload', { n: files.length }); addFiles(files, src || 'pick'); };
  if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el, (f, d) => handler((d && d.files) || (f ? [f] : []), d && d.source === 'drop' ? 'drop' : 'pick'));
  else el.addEventListener('tool-uploader:file', (e) => handler((e.detail && (e.detail.files || (e.detail.file && [e.detail.file]))) || [], 'pick'));
  el.addEventListener('tool-uploader:clear', () => { clearAll(); render(); });
})();
// a screenshot pasted anywhere on the page
document.addEventListener('paste', (e) => {
  if (e.target && e.target.closest && e.target.closest('input,textarea,[contenteditable]')) return;
  const items = Array.from((e.clipboardData && e.clipboardData.files) || []).filter((f) => /^image\//.test(f.type));
  if (items.length) { S.addMode = S.photos.length > 0; addFiles(items, 'paste'); }
});
renderSide();
// the worker and its modules load while the person is still choosing a photo
{ const w = engine.warm(); if (w !== true) diag({ issue: 'worker_start', msg: String((w && w.message) || w).slice(0, 80) }); }
// a read-only window into the state for the browser stand (tests/screen-photo-fix/*-e2e.mjs)
if (/[?&]spfdebug=1/.test(location.search)) window.__spfS = S;
